import { useCallback, useMemo } from "react"
import { useAuthStore } from "@/src/store"
import type { Permission } from "@/src/auth/permissions"
import { useCurrentRole } from "@/src/hooks/useCurrentRole"

export function usePermissions() {
    const permissions = useAuthStore((state) => state.user?.permissions ?? [])
    const { isInternal } = useCurrentRole()

    const permissionSet = useMemo(() => new Set(permissions), [permissions])

    const hasPermission = useCallback(
        (permission: Permission) => isInternal || permissionSet.has(permission),
        [isInternal, permissionSet]
    )

    const hasAnyPermission = useCallback(
        (requiredPermissions: Permission[]) =>
            isInternal || requiredPermissions.some((permission) => permissionSet.has(permission)),
        [isInternal, permissionSet]
    )

    const hasAllPermissions = useCallback(
        (requiredPermissions: Permission[]) =>
            isInternal || requiredPermissions.every((permission) => permissionSet.has(permission)),
        [isInternal, permissionSet]
    )

    return {
        permissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
    }
}

type UseHasPermissionOptions = {
    /** When multiple permissions are passed: "all" requires every one, "any" requires at least one. Defaults to "all". */
    mode?: "all" | "any"
}

/**
 * Returns a boolean for whether the current user holds the required permission(s).
 * Use directly in UI to decide what to render.
 *
 *   const canCreateUser = useHasPermission(Permission.createWorkspaceUser)
 *   const canManageJob = useHasPermission([Permission.createJob, Permission.updateJob])
 */
export function useHasPermission(
    requiredPermission: Permission | Permission[],
    options: UseHasPermissionOptions = {}
): boolean {
    const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()
    const { mode = "all" } = options

    if (Array.isArray(requiredPermission)) {
        return mode === "any" ? hasAnyPermission(requiredPermission) : hasAllPermissions(requiredPermission)
    }
    return hasPermission(requiredPermission)
}
